import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { find } from 'lodash';
import { StoreState } from '../../store/store';
import { selectExtensionByUUID } from '../extensionsDuck/extensionsDuck';
import { WorkflowState, WorkflowStateNode } from '../workflowDuck/workflowDuck';

export enum RIGHT_RAIL_TABS {
  RUNLOG,
  ADD_MODULE,
  EDIT_MODULE,
  CHAT,
}

export enum GraphNodeTypes {
  STATE = 1,
  MODULE, // 2
  JUNCTION, // 3
  GROUP_JUNCTION, // 4
  GROUP_EXIT, // 5
  CHILD, // 6
  TRIGGER, // 7
}

export type DraggableNodeTypes = Exclude<
  GraphNodeTypes,
  GraphNodeTypes.GROUP_EXIT | GraphNodeTypes.TRIGGER
>;

export interface NewGraphNodeItem extends WorkflowStateNode {
  __type: 'new';
  type: DraggableNodeTypes;
}

export interface ExistingGraphNodeItem extends WorkflowStateNode {
  __type: 'existing';
  type: DraggableNodeTypes;
}

export interface DesignerState {
  grabbedItem: NewGraphNodeItem | ExistingGraphNodeItem | undefined;
  activeTab: RIGHT_RAIL_TABS | null;
  showRunStepsPanel: boolean;
  // TODO: needs a new name. selectedElement could mean anything
  selectedElementID: string | undefined;
}

const initialState: DesignerState = {
  grabbedItem: undefined,
  activeTab: null,
  showRunStepsPanel: false,
  selectedElementID: undefined,
};

const DesignerSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    setGrabbedItem: (
      state,
      action: PayloadAction<DesignerState['grabbedItem']>
    ) => {
      state.grabbedItem = action.payload;
    },
    setActiveTab: (state, action) => {
      if (state.activeTab === action.payload) {
        state.activeTab = null;
      } else {
        state.activeTab = action.payload;
      }
    },
    setshowRunStepsPanel: (state, action) => {
      state.showRunStepsPanel = action.payload;
    },
    setSelectedElementID: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.selectedElementID = action.payload;
    },
  },
});

export const selectGrabbedItem = (state: StoreState) => {
  return state.designer.grabbedItem;
};

export const activeTabSelector = (state: StoreState) =>
  state.designer.activeTab;

export const showRunStepsPanelSelector = (state: StoreState) =>
  state.designer.showRunStepsPanel;

export const selectedElementSelector = (
  state: StoreState
  // TODO: need to type graph items. the return value here is any and so undefined checks are ignored by typescript
): WorkflowState['data']['graph']['items'] | undefined => {
  const selectedElementID = state.designer.selectedElementID;
  if (selectedElementID === 'trigger') {
    return state.workflow.data.graph.items[selectedElementID];
  }
  return selectedElementID
    ? state.workflow.data.nodes[selectedElementID]
    : undefined;
};

export const outputSelector = (state: StoreState) => {
  const selectedElement = selectedElementSelector(state);
  if (selectedElement && selectedElement.type === GraphNodeTypes.STATE) {
    return `{{states.${selectedElement.state}.value}}`;
  } else if (
    selectedElement &&
    selectedElement.type === GraphNodeTypes.MODULE
  ) {
    const extension = selectExtensionByUUID(selectedElement.pluginUUID)(state);
    const parentState = find(state.workflow.data.nodes, node => {
      if (node.next.includes(selectedElement.id)) {
        return true;
      }
      return false;
    });
    return parentState
      ? `{{states.${parentState.state}.${extension.name}.${selectedElement.moduleID}}}`
      : undefined;
  }
  return undefined;
};

export const {
  setshowRunStepsPanel,
  setActiveTab,
  setSelectedElementID,
  setGrabbedItem,
} = DesignerSlice.actions;
export default DesignerSlice.reducer;
